/* oxlint-disable typescript/no-non-null-assertion, unicorn/explicit-length-check, typescript/no-confusing-void-expression -- aggregate presence established by state branches; concise collection hot path */
import { hashString128 } from '@w/utils'
import type { Attrs } from './attrs'
import { createDDSketch, createDDSketchFromState } from './ddsketch'
import type { TelStorage } from './tel-storage'
import type {
  Exemplar,
  ExponentialHistogramAggregate,
  ExponentialHistogramMetricSeriesState,
  HistogramAggregate,
  HistogramDistribution,
  HistogramMetricSeriesState,
  GaugeMetricSeriesState,
  MetricKind,
  MetricResourceIdentity,
  MetricRuntimeOptions,
  MetricSeriesId,
  MetricSeriesIdentity,
  MetricSeriesState,
  MetricStreamId,
  MetricStreamIdentity,
  MetricStreamMeta,
  QuantileSummary,
  SumMetricSeriesState,
} from './tel-metrics-types'

export const DEFAULT_HISTOGRAM_BUCKETS = [0, 5, 10, 25, 50, 75, 100, 250, 500, 1_000, 2_500, 5_000, 10_000]

function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map((item) => canonical(item))
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).toSorted(([a], [b]) => a.localeCompare(b)).map(([key, item]) => [key, canonical(item)]),
    )
  }
  return value
}

function normalizedSeries<K extends MetricKind>(identity: MetricSeriesIdentity<K>): MetricSeriesIdentity<K> & { unit: string; attrs: Attrs } {
  return { resource: identity.resource, name: identity.name, kind: identity.kind, unit: identity.unit ?? '', attrs: identity.attrs ?? {} }
}

export function metricStreamId(identity: MetricStreamIdentity): MetricStreamId {
  const normalized = normalizedSeries(identity)
  const input = normalized.kind === 'gauge' ? normalized : { ...normalized, temporality: 'temporality' in identity ? identity.temporality : undefined }
  return hashString128(JSON.stringify(canonical(input)), 'tel-metric-stream-v1')
}

export function metricSeriesId(identity: MetricSeriesIdentity): MetricSeriesId {
  const { resource, name, kind, unit, attrs } = normalizedSeries(identity)
  return hashString128(JSON.stringify(canonical({ resource, name, kind, unit, attrs })), 'tel-metric-series-v1')
}

export interface BaseMetricOpts {
  name: string
  description?: string
  unit?: string
  attributes?: Attrs
  aggregationInterval?: number
}
export interface IntervalMetricOpts extends BaseMetricOpts {
  aggregationInterval: number
}
export interface CreateHistogramOpts extends IntervalMetricOpts {
  bucketBoundaries?: number[]
  percentileAccuracy?: number
  relativeAccuracy?: number
  metricSketchMaxBins?: number
}
export type MetricOpts = IntervalMetricOpts
export type GaugeMetricOpts = BaseMetricOpts
export type HistogramOpts = CreateHistogramOpts
type FactoryOpts = MetricRuntimeOptions & { mode?: 'server' | 'serverless' }
type ExemplarTrace = { traceId: string; spanId: string; requestId?: string; otel?: { traceId: string } }
type InstrumentConfig = { aggregationInterval?: number; bucketBoundaries?: number[]; relativeAccuracy?: number; maxBins: number }
type RuntimeSeries = { state: MetricSeriesState; interval: number; createdAt: number; streamIds: Partial<Record<'gauge' | 'delta' | 'cumulative', MetricStreamId>>; lastCollectedAt?: number; exemplars: Map<number, Exemplar>; idleCollections: number; recordedSinceCollection: boolean; hasCollected: boolean }

export function _createMetricsFactory(
  getStorage: () => TelStorage,
  resource: MetricResourceIdentity,
  opts: FactoryOpts = {},
) {
  const mode = opts.mode ?? 'server'
  const maxSeries = opts.metricSeriesLimit ?? 1_000
  const maxIdleCollections = opts.metricSeriesIdleIntervals ?? 10
  const maxBins = opts.metricSketchMaxBins ?? 2_048
  const series = new Map<MetricSeriesId, RuntimeSeries>()
  const instruments = new Map<string, InstrumentConfig>()
  const timers = new Map<number, ReturnType<typeof setInterval>>()

  function subscriptions() {
    return getStorage().metrics.subscriptions()
  }

  // The stream id is a pure function of the series identity and temporality, so it is hashed once per series.
  function streamMeta(runtime: RuntimeSeries, temporality?: 'delta' | 'cumulative'): MetricStreamMeta {
    const state = runtime.state
    const identity = temporality ? { ...state.identity, temporality } : state.identity
    const slot = temporality ?? 'gauge'
    const id = (runtime.streamIds[slot] ??= metricStreamId(identity as MetricStreamIdentity))
    return { ...identity, id, description: state.description } as MetricStreamMeta
  }

  // Series ids are hashed once per instrument + call attrs. The key is the attrs JSON in caller key order:
  // equal keys always mean equal canonical attrs, and a key order miss only costs one extra hash.
  const seriesKeys = new WeakMap<BaseMetricOpts, Map<string, { id: MetricSeriesId; identity: MetricSeriesIdentity }>>()
  function seriesKey<K extends MetricKind>(kind: K, input: BaseMetricOpts, attrs: Attrs | undefined): { id: MetricSeriesId; identity: MetricSeriesIdentity<K> } {
    let byAttrs = seriesKeys.get(input)
    if (!byAttrs) seriesKeys.set(input, (byAttrs = new Map<string, { id: MetricSeriesId; identity: MetricSeriesIdentity }>()))
    const key = attrs === undefined ? kind : `${kind}:${JSON.stringify(attrs)}`
    let entry = byAttrs.get(key)
    if (!entry) {
      const identity: MetricSeriesIdentity<K> = { resource, name: input.name, kind, unit: input.unit, attrs: { ...input.attributes, ...attrs } }
      entry = { id: metricSeriesId(identity), identity }
      if (byAttrs.size >= maxSeries) byAttrs.clear()
      byAttrs.set(key, entry)
    }
    return entry as { id: MetricSeriesId; identity: MetricSeriesIdentity<K> }
  }

  function ensureTimer(interval: number): void {
    if (mode === 'serverless' || timers.has(interval)) return
    const timer = setInterval(() => collect(interval), interval)
    timer.unref?.()
    timers.set(interval, timer)
  }

  function configFor(kind: MetricKind, input: BaseMetricOpts | CreateHistogramOpts): { interval: number; config: InstrumentConfig } {
    let interval = input.aggregationInterval
    if (mode === 'server' && kind !== 'gauge' && interval === undefined) {
      throw new Error(`${kind} aggregationInterval is required in server mode`)
    }
    interval ??= kind === 'gauge' ? 60_000 : 0
    const histogram = input as CreateHistogramOpts
    return {
      interval,
      config: {
        aggregationInterval: input.aggregationInterval,
        bucketBoundaries: histogram.bucketBoundaries,
        relativeAccuracy: histogram.relativeAccuracy ?? histogram.percentileAccuracy,
        maxBins: histogram.metricSketchMaxBins ?? maxBins,
      },
    }
  }

  function register(kind: MetricKind, input: BaseMetricOpts | CreateHistogramOpts): number {
    const { interval, config } = configFor(kind, input)
    const identity = metricSeriesId({ resource, name: input.name, kind, unit: input.unit, attrs: input.attributes })
    const previous = instruments.get(identity)
    if (previous && JSON.stringify(previous) !== JSON.stringify(config)) {
      throw new Error(`Conflicting metric instrument config for ${input.name}`)
    }
    instruments.set(identity, config)
    ensureTimer(interval)
    return interval
  }

  function resolve<K extends MetricKind>(kind: K, input: BaseMetricOpts, attrs: Attrs | undefined, interval: number, create: (id: MetricSeriesId, identity: MetricSeriesIdentity<K>, now: number) => MetricSeriesState): RuntimeSeries | undefined {
    const { id, identity } = seriesKey(kind, input, attrs)
    let runtime = series.get(id)
    if (!runtime) {
      if (series.size >= maxSeries) {
        getStorage().metrics.reportDrop()
        return undefined
      }
      const now = Date.now()
      runtime = { state: create(id, identity, now), interval, createdAt: now, streamIds: {}, exemplars: new Map(), idleCollections: 0, recordedSinceCollection: false, hasCollected: false }
      series.set(id, runtime)
      ensureTimer(interval)
    }
    if (input.description !== undefined) runtime.state.description = input.description
    return runtime
  }

  function find(kind: MetricKind, input: BaseMetricOpts, attrs?: Attrs): RuntimeSeries | undefined {
    return series.get(seriesKey(kind, input, attrs).id)
  }

  function emptyHistogram(bounds: number[], relativeAccuracy: number, bins: number, startedAt: number): HistogramAggregate {
    return {
      startedAt,
      distribution: { bounds: [...bounds], counts: Array.from({ length: bounds.length + 1 }, () => 0), stats: { count: 0, sum: 0 } },
      quantileSketch: createDDSketch(relativeAccuracy, bins).toState(),
    }
  }

  function emptyExponential(relativeAccuracy: number, bins: number, startedAt: number): ExponentialHistogramAggregate {
    return {
      startedAt,
      distribution: { scale: 0, zeroCount: 0, positive: { offset: 0, counts: [] }, negative: { offset: 0, counts: [] }, stats: { count: 0, sum: 0 } },
      quantileSketch: createDDSketch(relativeAccuracy, bins).toState(),
    }
  }

  function collect(filterInterval?: number, filterSeriesId?: MetricSeriesId): void {
    const now = Date.now()
    const subs = subscriptions()
    for (const [id, runtime] of series) {
      if (filterInterval !== undefined && runtime.interval !== filterInterval) continue
      if (filterSeriesId !== undefined && id !== filterSeriesId) continue
      if (runtime.recordedSinceCollection) {
        runtime.recordedSinceCollection = false
        runtime.idleCollections = 0
      } else if (++runtime.idleCollections >= maxIdleCollections) {
        series.delete(id)
        continue
      }
      runtime.hasCollected = true
      const state = runtime.state
      if (state.identity.kind === 'gauge') {
        const gaugeState = state as GaugeMetricSeriesState
        if (subs.gauge) getStorage().metrics.create({ streamMeta: streamMeta(runtime) as MetricStreamMeta & { kind: 'gauge' }, collectedAt: now, value: gaugeState.value })
        continue
      }
      const temporalState = state as SumMetricSeriesState | HistogramMetricSeriesState | ExponentialHistogramMetricSeriesState
      if (subs.delta && temporalState.delta) {
        if (state.identity.kind === 'counter' || state.identity.kind === 'upDownCounter') {
          const delta = (temporalState as SumMetricSeriesState).delta!
          getStorage().metrics.create({ streamMeta: streamMeta(runtime, 'delta') as never, periodStartAt: delta.startedAt, collectedAt: now, value: delta.value })
          temporalState.delta = { startedAt: now, value: 0 }
        } else if (state.identity.kind === 'histogram') {
          const delta = (temporalState as HistogramMetricSeriesState).delta!
          const histogram = structuredClone(delta.distribution)
          if (runtime.exemplars.size) histogram.exemplars = [...runtime.exemplars.values()]
          getStorage().metrics.create({ streamMeta: streamMeta(runtime, 'delta') as never, periodStartAt: delta.startedAt, collectedAt: now, histogram, quantileSketch: structuredClone(delta.quantileSketch) })
          temporalState.delta = emptyHistogram(delta.distribution.bounds, delta.quantileSketch.relativeAccuracy, delta.quantileSketch.maxBins, now)
        } else {
          const delta = (temporalState as ExponentialHistogramMetricSeriesState).delta!
          getStorage().metrics.create({ streamMeta: streamMeta(runtime, 'delta') as never, periodStartAt: delta.startedAt, collectedAt: now, exponentialHistogram: structuredClone(delta.distribution), quantileSketch: structuredClone(delta.quantileSketch) })
          temporalState.delta = emptyExponential(delta.quantileSketch.relativeAccuracy, delta.quantileSketch.maxBins, now)
        }
      }
      if (subs.cumulative && temporalState.cumulative) {
        if (state.identity.kind === 'counter' || state.identity.kind === 'upDownCounter') {
          const cumulative = (temporalState as SumMetricSeriesState).cumulative!
          getStorage().metrics.create({ streamMeta: streamMeta(runtime, 'cumulative') as never, startedAt: cumulative.startedAt, collectedAt: now, value: cumulative.value })
        } else if (state.identity.kind === 'histogram') {
          const cumulative = (temporalState as HistogramMetricSeriesState).cumulative!
          const histogram = structuredClone(cumulative.distribution)
          if (runtime.exemplars.size) histogram.exemplars = [...runtime.exemplars.values()]
          getStorage().metrics.create({ streamMeta: streamMeta(runtime, 'cumulative') as never, startedAt: cumulative.startedAt, collectedAt: now, histogram, quantileSketch: structuredClone(cumulative.quantileSketch) })
        } else {
          const cumulative = (temporalState as ExponentialHistogramMetricSeriesState).cumulative!
          getStorage().metrics.create({ streamMeta: streamMeta(runtime, 'cumulative') as never, startedAt: cumulative.startedAt, collectedAt: now, exponentialHistogram: structuredClone(cumulative.distribution), quantileSketch: structuredClone(cumulative.quantileSketch) })
        }
      }
      runtime.exemplars.clear()
      if (!subs.delta) temporalState.delta = undefined
      if (!subs.cumulative) temporalState.cumulative = undefined
      runtime.lastCollectedAt = now
    }
    if (filterInterval !== undefined && ![...series.values()].some((runtime) => runtime.interval === filterInterval)) {
      const timer = timers.get(filterInterval)
      if (timer) clearInterval(timer)
      timers.delete(filterInterval)
    }
  }

  function updateStats(stats: HistogramDistribution['stats'], value: number): void {
    stats.count++
    stats.sum += value
    stats.minMax = stats.minMax ? { min: Math.min(stats.minMax.min, value), max: Math.max(stats.minMax.max, value) } : { min: value, max: value }
  }

  function updateHistogram(aggregate: HistogramAggregate, value: number): void {
    const index = aggregate.distribution.bounds.findIndex((bound) => value <= bound)
    aggregate.distribution.counts[index < 0 ? aggregate.distribution.bounds.length : index]++
    updateStats(aggregate.distribution.stats, value)
    const sketch = createDDSketchFromState(aggregate.quantileSketch)
    sketch.add(value)
    aggregate.quantileSketch = sketch.toState()
  }

  function updateExponential(aggregate: ExponentialHistogramAggregate, value: number): void {
    const distribution = aggregate.distribution
    updateStats(distribution.stats, value)
    if (value === 0) distribution.zeroCount++
    else {
      const target = value > 0 ? distribution.positive : distribution.negative
      const index = Math.floor(Math.log2(Math.abs(value)))
      if (!target.counts.length) target.offset = index
      if (index < target.offset) {
        target.counts.unshift(...Array.from({ length: target.offset - index }, () => 0))
        target.offset = index
      }
      while (target.counts.length <= index - target.offset) target.counts.push(0)
      target.counts[index - target.offset]++
      while (distribution.positive.counts.length + distribution.negative.counts.length > aggregate.quantileSketch.maxBins) {
        const bucket = distribution.negative.counts.length ? distribution.negative : distribution.positive
        const removed = bucket.counts.shift() ?? 0
        bucket.offset++
        if (bucket.counts.length) bucket.counts[0] += removed
      }
    }
    const sketch = createDDSketchFromState(aggregate.quantileSketch)
    sketch.add(value)
    aggregate.quantileSketch = sketch.toState()
  }

  function sum(kind: 'counter' | 'upDownCounter', input: IntervalMetricOpts) {
    const interval = register(kind, input)
    const get = (attrs?: Attrs) => resolve(kind, input, attrs, interval, (id, identity, now): SumMetricSeriesState => ({ id, identity, description: input.description, lastRecordedAt: now }))
    return {
      add(value: number, attrs?: Attrs) {
        if (kind === 'counter' && value < 0) throw new Error('Counter values must be non-negative')
        const runtime = get(attrs)
        if (!runtime) return
        const state = runtime.state as SumMetricSeriesState
        const subs = subscriptions()
        state.lastRecordedAt = Date.now()
        if (runtime.hasCollected) runtime.recordedSinceCollection = true
        if (subs.cumulative) (state.cumulative ??= { startedAt: runtime.createdAt, value: 0 }).value += value
        else state.cumulative = undefined
        if (subs.delta) (state.delta ??= { startedAt: runtime.lastCollectedAt ?? runtime.createdAt, value: 0 }).value += value
        else state.delta = undefined
        if (mode === 'serverless') collect(interval, runtime.state.id)
      },
      getValue(attrs?: Attrs) { const state = find(kind, input, attrs)?.state as SumMetricSeriesState | undefined; return state?.cumulative?.value ?? state?.delta?.value ?? 0 },
      clear() { for (const [id, runtime] of series) if (runtime.state.identity.name === input.name && runtime.state.identity.kind === kind) series.delete(id) },
    }
  }

  function gauge(input: BaseMetricOpts) {
    const interval = register('gauge', input)
    const set = (value: number, attrs?: Attrs) => {
      const runtime = resolve('gauge', input, attrs, interval, (id, identity, now) => ({ id, identity, description: input.description, lastRecordedAt: now, value }))
      if (!runtime) return
      const state = runtime.state as GaugeMetricSeriesState
      state.value = value
      state.lastRecordedAt = Date.now()
      if (runtime.hasCollected) runtime.recordedSinceCollection = true
      if (mode === 'serverless') collect(interval, runtime.state.id)
    }
    return { set, record: set, getValue(attrs?: Attrs) { return (find('gauge', input, attrs)?.state as { value: number } | undefined)?.value ?? 0 } }
  }

  function histogram(input: CreateHistogramOpts) {
    const interval = register('histogram', input)
    const bounds = input.bucketBoundaries ?? DEFAULT_HISTOGRAM_BUCKETS
    const accuracy = input.relativeAccuracy ?? input.percentileAccuracy ?? 0.01
    const bins = input.metricSketchMaxBins ?? maxBins
    const get = (attrs?: Attrs) => resolve('histogram', input, attrs, interval, (id, identity, now): HistogramMetricSeriesState => ({ id, identity, description: input.description, lastRecordedAt: now }))
    const record = (value: number, attrs?: Attrs, exemplar?: ExemplarTrace) => {
      const runtime = get(attrs)
      if (!runtime) return
      const state = runtime.state as HistogramMetricSeriesState
      const subs = subscriptions()
      state.lastRecordedAt = Date.now()
      if (runtime.hasCollected) runtime.recordedSinceCollection = true
      if (subs.cumulative) updateHistogram(state.cumulative ??= emptyHistogram(bounds, accuracy, bins, runtime.createdAt), value)
      else state.cumulative = undefined
      if (subs.delta) updateHistogram(state.delta ??= emptyHistogram(bounds, accuracy, bins, runtime.lastCollectedAt ?? runtime.createdAt), value)
      else state.delta = undefined
      if (exemplar) {
        const bucketIndex = bounds.findIndex((bound) => value <= bound)
        const effective = state.identity.attrs ?? {}
        const filteredAttrs = Object.fromEntries(Object.entries(attrs ?? {}).filter(([key]) => !(key in effective)))
        runtime.exemplars.set(bucketIndex < 0 ? bounds.length : bucketIndex, { value, timestamp: Date.now(), trace: exemplar, ...(Object.keys(filteredAttrs).length ? { filteredAttrs } : {}) })
      }
      if (mode === 'serverless') collect(interval, runtime.state.id)
    }
    return {
      record: (value: number, attrs?: Attrs) => record(value, attrs),
      _recordWithExemplar: (value: number, attrs: Attrs | undefined, exemplar: ExemplarTrace) => record(value, attrs, exemplar),
      recordDuration<R>(cb: () => R | Promise<R>, attrs?: Attrs): R | Promise<R> {
        const start = performance.now()
        const result = cb()
        if (result instanceof Promise) return result.then((output) => (record(performance.now() - start, attrs), output))
        record(performance.now() - start, attrs)
        return result
      },
      getPercentiles(attrs?: Attrs): QuantileSummary | undefined {
        const state = find('histogram', input, attrs)?.state as HistogramMetricSeriesState | undefined
        const aggregate = state?.cumulative ?? state?.delta
        return aggregate?.distribution.stats.count ? createDDSketchFromState(aggregate.quantileSketch).getPercentiles() : undefined
      },
      getStatistics(attrs?: Attrs) {
        const state = find('histogram', input, attrs)?.state as HistogramMetricSeriesState | undefined
        const aggregate = state?.cumulative ?? state?.delta
        return aggregate ? { ...aggregate.distribution.stats, percentiles: aggregate.distribution.stats.count ? createDDSketchFromState(aggregate.quantileSketch).getPercentiles() : undefined } : { count: 0, sum: 0, percentiles: undefined }
      },
      clear() { for (const [id, runtime] of series) if (runtime.state.identity.name === input.name && runtime.state.identity.kind === 'histogram') series.delete(id) },
    }
  }

  function exponentialHistogram(input: IntervalMetricOpts) {
    const interval = register('exponentialHistogram', input)
    const accuracy = 0.01
    const get = (attrs?: Attrs) => resolve('exponentialHistogram', input, attrs, interval, (id, identity, now): ExponentialHistogramMetricSeriesState => ({ id, identity, description: input.description, lastRecordedAt: now }))
    return { record(value: number, attrs?: Attrs) {
      const runtime = get(attrs)
      if (!runtime) return
      const state = runtime.state as ExponentialHistogramMetricSeriesState
      const subs = subscriptions()
      state.lastRecordedAt = Date.now()
      if (runtime.hasCollected) runtime.recordedSinceCollection = true
      if (subs.cumulative) updateExponential(state.cumulative ??= emptyExponential(accuracy, maxBins, runtime.createdAt), value)
      else state.cumulative = undefined
      if (subs.delta) updateExponential(state.delta ??= emptyExponential(accuracy, maxBins, runtime.lastCollectedAt ?? runtime.createdAt), value)
      else state.delta = undefined
      if (mode === 'serverless') collect(interval, runtime.state.id)
    } }
  }

  function normalize<T extends BaseMetricOpts>(nameOrOpts: string | T, extra?: Omit<T, 'name'>): T {
    return (typeof nameOrOpts === 'string' ? { ...extra, name: nameOrOpts } : nameOrOpts) as T
  }

  function counter(input: IntervalMetricOpts): ReturnType<typeof sum>
  function counter(name: string, extra: Omit<IntervalMetricOpts, 'name'>): ReturnType<typeof sum>
  function counter(nameOrOpts: string | IntervalMetricOpts, extra?: Omit<IntervalMetricOpts, 'name'>) {
    return sum('counter', normalize(nameOrOpts, extra))
  }

  function upDownCounter(input: IntervalMetricOpts): ReturnType<typeof sum>
  function upDownCounter(name: string, extra: Omit<IntervalMetricOpts, 'name'>): ReturnType<typeof sum>
  function upDownCounter(nameOrOpts: string | IntervalMetricOpts, extra?: Omit<IntervalMetricOpts, 'name'>) {
    return sum('upDownCounter', normalize(nameOrOpts, extra))
  }

  function createHistogram(input: CreateHistogramOpts): ReturnType<typeof histogram>
  function createHistogram(name: string, extra: Omit<CreateHistogramOpts, 'name'>): ReturnType<typeof histogram>
  function createHistogram(nameOrOpts: string | CreateHistogramOpts, extra?: Omit<CreateHistogramOpts, 'name'>) {
    return histogram(normalize(nameOrOpts, extra))
  }

  function createExponentialHistogram(input: IntervalMetricOpts): ReturnType<typeof exponentialHistogram>
  function createExponentialHistogram(name: string, extra: Omit<IntervalMetricOpts, 'name'>): ReturnType<typeof exponentialHistogram>
  function createExponentialHistogram(nameOrOpts: string | IntervalMetricOpts, extra?: Omit<IntervalMetricOpts, 'name'>) {
    return exponentialHistogram(normalize(nameOrOpts, extra))
  }

  return {
    counter,
    upDownCounter,
    gauge: (nameOrOpts: string | BaseMetricOpts, extra?: Omit<BaseMetricOpts, 'name'>) => gauge(normalize(nameOrOpts, extra)),
    histogram: createHistogram,
    exponentialHistogram: createExponentialHistogram,
    _getSeriesState: (kind: MetricKind, input: BaseMetricOpts, attrs?: Attrs) => find(kind, input, attrs)?.state,
    flushAggregators: () => {
      if (mode === 'server') collect()
    },
    clearAggregators() {
      for (const timer of timers.values()) clearInterval(timer)
      timers.clear()
      series.clear()
    },
  }
}
